import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Доступ к камере смартфона или веб-камере (FR-MGR-01).
 * getUserMedia требует защищённого контекста (HTTPS или localhost).
 */
export type CameraErrorCode =
  | 'no_device'
  | 'permission_denied'
  | 'insecure_context'
  | 'unavailable'
  | 'unknown';

export interface CameraState {
  status: 'idle' | 'starting' | 'active' | 'error';
  error: CameraErrorCode | null;
}

export interface CameraController {
  state: CameraState;
  start: () => Promise<void>;
  stop: () => void;
}

export function useCamera(
  videoRef: RefObject<HTMLVideoElement>,
  active: boolean,
): CameraController {
  const [state, setState] = useState<CameraState>({ status: 'idle', error: null });
  const streamRef = useRef<MediaStream | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setState((previous) => (previous.status === 'idle' ? previous : { status: 'idle', error: null }));
  }, [videoRef]);

  const start = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setState({ status: 'error', error: 'unavailable' });
      return;
    }
    if (!window.isSecureContext) {
      setState({ status: 'error', error: 'insecure_context' });
      return;
    }

    setState({ status: 'starting', error: null });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        video.muted = true;
        try {
          await video.play();
        } catch {
          // Автозапуск может быть заблокирован — управление кнопкой ниже.
        }
      }
      setState({ status: 'active', error: null });
    } catch (error) {
      const name = (error as { name?: string } | null)?.name ?? '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setState({ status: 'error', error: 'permission_denied' });
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setState({ status: 'error', error: 'no_device' });
      } else {
        setState({ status: 'error', error: 'unknown' });
      }
    }
  }, [videoRef]);

  useEffect(() => {
    if (active) {
      void start();
    } else {
      stop();
    }
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [active, start]);

  return { state, start, stop };
}
